/**
 * ScanRecoveryBanner — FE #124.4.
 *
 * Mounted at the top of DashboardScreen (above any recall banners but
 * below the brand top-bar). Renders when the user has ≥1 unfinished
 * scan (status IN_PROGRESS or READY_TO_SAVE).
 *
 * Two variants:
 *
 * Single (N == 1):
 *   ┌────────────────────────────────────────────┐
 *   │ 📷  Unfinished scan from 3 min ago         │
 *   │     8 items detected • not saved yet       │
 *   │                              [Resume →]   │
 *   └────────────────────────────────────────────┘
 *   Tap → SegmentScreen with file_record_id loaded (FE #124.6).
 *
 * Multi (N > 1):
 *   ┌────────────────────────────────────────────┐
 *   │ 📷  3 unfinished scans                     │
 *   │     Resume or discard from Scan history    │
 *   │                         [View all 3 →]    │
 *   └────────────────────────────────────────────┘
 *   Tap → /scans (ScanHistory page).
 *
 * Visual language mirrors RecallBanner (the established compact-banner
 * pattern on Dashboard from FE #198 Sub 3). Primary container surface,
 * leading icon, two lines of text, trailing CTA chevron.
 */

import React, { useMemo } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { fpColors } from '../../../styles/theme';
import type { ScanHistoryItem } from '../../../api/types';

interface ScanRecoveryBannerProps {
  /** Sorted by created_at desc — most recent unfinished scan first.
   *  Empty array hides the banner entirely (parent typically gates
   *  on `unfinished.length > 0` before mounting). */
  unfinished: ScanHistoryItem[];
  /** Tap handler for the single-scan variant. Receives the
   *  file_record_id of the (only) unfinished scan so the parent can
   *  navigate to SegmentScreen with the right params. */
  onResumeSingle: (fileRecordId: number) => void;
  /** Tap handler for the multi-scan variant — opens /scans. */
  onViewAll: () => void;
  /** Single-scan variant only — discard the (only) unfinished scan.
   *  Renders a visible trash button (web + phone-web). When omitted,
   *  no discard affordance shows. */
  onDiscardSingle?: (fileRecordId: number) => void;
}

/** Same relative-time formatter used in ScanRow + #111. */
function relativeMinutes(iso: string): string {
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return '';
  const ms = Date.now() - ts;
  if (ms < 60_000) return 'just now';
  if (ms < 60 * 60_000) return `${Math.floor(ms / 60_000)} min ago`;
  if (ms < 24 * 60 * 60_000) return `${Math.floor(ms / (60 * 60_000))}h ago`;
  if (ms < 7 * 24 * 60 * 60_000)
    return `${Math.floor(ms / (24 * 60 * 60_000))}d ago`;
  return new Date(ts).toLocaleDateString();
}

export const ScanRecoveryBanner: React.FC<ScanRecoveryBannerProps> = ({
  unfinished,
  onResumeSingle,
  onViewAll,
  onDiscardSingle,
}) => {
  const count = unfinished.length;

  /** Render nothing when there's nothing to recover. Parent should
   *  also gate this, but the double-guard keeps an empty render
   *  cheap if a caller forgets. */
  if (count === 0) return null;

  // Single-scan variant pulls metadata from the (only) scan; multi
  // doesn't need per-scan info.
  const single = count === 1 ? unfinished[0] : null;

  const subtitle = useMemo(() => {
    if (single) {
      const items = single.detected_count;
      const itemsStr =
        items === 1 ? '1 item detected' : `${items} items detected`;
      return `${itemsStr} • not saved yet`;
    }
    // Shortened from "Resume or discard from Scan history" — that
    // copy truncated to "Resume or discard from S..." at 390px wide
    // when paired with the [View all N] CTA. Tap-to-open carries
    // the same affordance.
    return 'Tap to resume or discard';
  }, [single]);

  const title = useMemo(() => {
    if (single) {
      return `Unfinished scan from ${relativeMinutes(single.last_event_at)}`;
    }
    return `${count} unfinished scans`;
  }, [single, count]);

  // Shorten the CTA on the multi-variant — "View all 6" is already
  // self-explanatory; the count alone doesn't add information once
  // the title above says "N unfinished scans".
  const ctaLabel = single ? 'Resume' : 'View all';

  return (
    // testID on the OUTER card so it measures the full banner width (the
    // visible card spans the scroll's 16px inset, same as the requests
    // banner — #160). The inner resume Pressable is narrower because the
    // Discard button is its sibling; measuring it would falsely fail the
    // #160 equal-width check (it did, until this was moved out).
    <View style={styles.row} testID="dashboard-scan-recovery-banner">
      {/* Main tap target — resume (single) / view-all (multi). */}
      <Pressable
        onPress={() => {
          if (single) onResumeSingle(single.file_record_id);
          else onViewAll();
        }}
        accessibilityRole="button"
        accessibilityLabel={`${title}. ${subtitle}. ${ctaLabel}.`}
        testID="dashboard-scan-recovery-resume"
        style={({ pressed }) => [styles.main, pressed && { opacity: 0.85 }]}
      >
        {/* Leading icon — same iconography as the [+ Add New Item] sheet's
            "Take photo" row so the user has a visual link between the
            banner and the action it implies. */}
        <View style={styles.iconWrap}>
          <MaterialIcons
            name="photo-camera"
            size={20}
            color={fpColors.primary}
          />
        </View>

        <View style={styles.body}>
          <Text style={styles.title} numberOfLines={1}>
            {title}
          </Text>
          <Text style={styles.subtitle} numberOfLines={1}>
            {subtitle}
          </Text>
        </View>

        <View style={styles.ctaWrap}>
          {/* onPrimaryContainer (light) — `primary` was purple-on-purple
              against the primaryContainer bg (~1.8:1, barely visible). */}
          <Text style={styles.ctaText}>{ctaLabel}</Text>
          <MaterialIcons
            name="chevron-right"
            size={20}
            color={fpColors.onPrimaryContainer}
          />
        </View>
      </Pressable>

      {/* Single-scan: a visible Discard button (web + phone-web), NOT a
          gesture. Multi routes to /scans via the main tap, where each
          scan is discarded individually. */}
      {single && onDiscardSingle ? (
        <Pressable
          onPress={() => onDiscardSingle(single.file_record_id)}
          hitSlop={8}
          accessibilityRole="button"
          accessibilityLabel="Discard scan"
          testID="dashboard-scan-discard"
          style={({ pressed }) => [
            styles.discardBtn,
            pressed && { opacity: 0.7 },
          ]}
        >
          <MaterialIcons
            name="delete-outline"
            size={18}
            color={fpColors.error}
          />
        </Pressable>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  // #160 — Match the FE-213 attentionBanner box model exactly so the
  // two Dashboard banners are the SAME width and corner radius when
  // stacked. Both now rely on the parent scroll's 16px padding for the
  // horizontal inset (no baked-in marginHorizontal — that was making
  // this banner ~32px narrower than the recall/requests banner below
  // it). paddingHorizontal 14 + borderRadius 14 + marginBottom 16 are
  // the attentionBanner values; keep them in lockstep.
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 14,
    paddingVertical: 12,
    marginBottom: 16,
    backgroundColor: fpColors.primaryContainer,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
  },
  // Inner resume / view-all tap target (icon + body + cta).
  main: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  // Trailing discard button — surface chip so the destructive trash
  // reads as a distinct button on the purple banner.
  discardBtn: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: fpColors.surface,
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconWrap: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: fpColors.surface,
    alignItems: 'center',
    justifyContent: 'center',
  },
  body: { flex: 1 },
  title: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: fpColors.onPrimaryContainer,
  },
  subtitle: {
    marginTop: 2,
    fontFamily: 'Inter_400Regular',
    fontSize: 12,
    color: fpColors.onPrimaryContainer,
    opacity: 0.85,
  },
  ctaWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
  },
  ctaText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 12,
    color: fpColors.onPrimaryContainer,
  },
});
